import { GridReadyEvent, ICellRendererParams, ViewportChangedEvent } from '@ag-grid-community/core';
import {
	Component, HostBinding, OnDestroy, ViewEncapsulation
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';
import moment from 'moment';
import { catchError, firstValueFrom, forkJoin, fromEvent, of, Subscription, switchMap, tap } from 'rxjs';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

import {
	Document,
	DOCUMENTS_SUB_ENTITY,
	FieldOfDocument,
	GlobalDocument
} from '../../../commercial-documents/models/document';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import { Order, orderCardFields } from '../../../order/models/order';
import { OrderService } from '../../../order/services/order.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import {
	CommonColDef, DateColDef, IconRedirectColDef, LinkColDef, NumericColDef
} from '../../../shared/datacenter/column-defs';
import { clientSideAgGridModules, CommonGridOptions } from '../../../shared/datacenter/grid-options';
import { CURRENCY_OPTIONS, DEFECT_DETAILED_OPTIONS, UNIT_OPTIONS } from '../../../shared/models/list';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { User } from '../../../shared/models/user';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { ARCELOR_SUPPLIER_DESC, OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { QualityIssuePendingPushStatus, QualityIssueStatus } from '../../models/all-quality-issue-constants';
import { ClaimBatch } from '../../models/claim-batch';
import { QualityBatchLine } from '../../models/quality-batch-line';
import { QualityAttachment } from '../../models/quality-attachment';
import {
	CLAIM_TYPE_TR,
	CLOSED_STATUS_LIST,
	DECISION_TR,
	ISSUE_TYPE_TR,
	QualityIssue,
	STATUS_TR
} from '../../models/quality-issue';
import { AllQualityIssueService } from '../../services/all-quality-issue.service';
import { ClaimBatchesService } from '../../services/batches.service';
import { QualityIssueService } from '../../services/quality-issue.service';
import { formatHiddenNumber, formatNumber } from '../../../shared/utils/number.format';
import { ModelInfoPipe } from '../../../shared/pipes/model-info.pipe';
import { TextToNumberPipe } from '../../../shared/pipes/texttonumber.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { CharacPipe } from '../../../shared/pipes/characteristic.pipe';
import { Characteristic } from '../../../order/models/characteristic';

import { DialogCancelIssueComponent, RESPONSE_CONFIRM } from './dialog-cancel-issue.component';
import { SnackbarCancelIssueComponent } from './snackbar-cancel-issue.component';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';

const resizeObservable = fromEvent(globalThis, 'resize');

const linkToBatchCardCellRendererParams = {
	routerLink(params: ICellRendererParams<QualityBatchLine, unknown>) {
		const qualityBatchLine = params.data;
		return qualityBatchLine?.claimBatch && qualityBatchLine.orderBatch
			? { commands: [
				'/order-tracking',
				'batch',
				'card',
				qualityBatchLine.claimBatch.id,
			] }
			: undefined;
	}
};

@Component({
	selector: 'copo-issue-card',
	templateUrl: './issue-card.component.html',
	styleUrl: './issue-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		BackButtonComponent,
		TranslateModule,

		MatButtonModule,
		RouterLink,
		MatIconModule,
		MatExpansionModule,
		MatDividerModule,
		AgGridModule,
		AfterViewInitDirective,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe,
		TextToNumberPipe,
		CharacPipe,
		ModelInfoPipe
	]
})
export class IssueCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	issue?: QualityIssue;
	remoteIssueId: string;
	order: Order | undefined;
	claimSub?: Subscription;
	resizeSub?: Subscription;
	cancelIssueSubscription?: Subscription;
	isLoading = false;
	isMainLoading = false;

	invoiceDisplayed = false;

	attachmentsGridOptions?: CommonGridOptions<QualityAttachment>;
	gridCommercialDocumentsOptions?: CommonGridOptions<GlobalDocument>;
	gridBatchesOptions?: CommonGridOptions<QualityBatchLine>;
	orderCharacteristics?: Characteristic[];
	documentsFields: readonly FieldOfDocument[] = [
		'id',
		'subtype',
		'type',
		'date',
		'name',
		'millCode',
		`${ DOCUMENTS_SUB_ENTITY }.vendorBatchId`,
		`${ DOCUMENTS_SUB_ENTITY }.millCode`
	];

	readonly DEFECT_DETAILED_OPTIONS = DEFECT_DETAILED_OPTIONS;
	readonly UNIT_OPTIONS = UNIT_OPTIONS;
	readonly TYPE_TR_OPTIONS = CLAIM_TYPE_TR;
	readonly UNDERSCORE = UNDERSCORE;
	readonly ARCELOR_SUPPLIER_DESC = ARCELOR_SUPPLIER_DESC;

	readonly agGridModules = clientSideAgGridModules;

	user: User;
	private readonly id: string | null;

	constructor(
		private readonly router: Router,
		private readonly qualityIssueService: QualityIssueService,
		private readonly allqualityIssueService: AllQualityIssueService,
		private readonly route: ActivatedRoute,
		private readonly orderService: OrderService,
		private readonly claimBatchesService: ClaimBatchesService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly commercialDocumentService: DocumentService,

		private readonly dialog: MatDialog,

		private readonly snackBar: MatSnackBar,
	) {
		this.isLoading = true;
		this.isMainLoading = true;
		this.remoteIssueId = <string> this.route.snapshot.params['issueid'];
		this.id = route.snapshot.fragment;

		this.user = this.userService.user;
		this.invoiceDisplayed = this.userService.user.hasServiceParameterEqualsTo('PDOCS', 'Invoice', 'True');
		this.claimSub = this.qualityIssueService.getClaimByID(this.remoteIssueId)
			.pipe(
				tap(issue => {
					this.issue = issue;
				}),
				switchMap(issue => {
					const supplierRef = issue.supplierOrderNumber || '';
					const supplierItem = issue.supplierOrderItemNumber
						? String(issue.supplierOrderItemNumber)
						: '';
					return forkJoin([
						of(issue),
						this.orderService.getOrderCard(
							supplierRef,
							supplierItem,
							orderCardFields
						)
							.pipe(
								catchError((err: unknown) => {
									console.error('Order not found :', err);
									return of(undefined);
								}),
								tap(order => {
									this.order = order;
									this.isMainLoading = false;
								})
							),
						this.claimBatchesService.getBatchesForIssueBatches(issue.batches),
						this.invoiceDisplayed && issue.supplierIssueNumber
							? this.commercialDocumentService.getDocuments(
								this.documentsFields,
								this.constructDocumentFilter(issue.supplierIssueNumber),
								0,
								100,
								new OrderBy(SORT_BY, SORT_WAY),
								false
							)
							: of(undefined),
						this.orderService.getOrderCharacteritics(
							supplierRef,
							supplierItem
						)
					]);
				})
			).subscribe({
				next: ([_issue, _order, batches, invoices, orderCharacteristicsResult]) => {
					this.constructBatchesGrid(batches);
					this.constructCommercialDocuments(invoices);
					if (this.issue) {
						this.constructAttachements(this.issue.attachments);
					}
					this.orderCharacteristics = orderCharacteristicsResult.results;
					this.isLoading = false;
				},
				error: (err: unknown) => {
					this.isLoading = false;
					this.isMainLoading = false;
					console.error('error can\'t retrieve issue', err);
				}
			});
	}

	absoluteValue(value: number | undefined) {
		return value !== undefined ? Math.abs(value) : value;
	}

	ngOnDestroy() {
		if (this.claimSub) {
			this.claimSub.unsubscribe();
		}
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
	}

	evtCancelIssue(issue: QualityIssue) {
		const sub = this.dialog.open<DialogCancelIssueComponent, number>(
			DialogCancelIssueComponent,
			{ panelClass: 'copo-claim-dialog' }
		);
		sub.componentInstance.issueName = issue.customerReference;
		this.cancelIssueSubscription = sub.afterClosed()
			.subscribe(async response => {
				if (response === RESPONSE_CONFIRM) {
					const path = `/quality-issues/card/${ issue.id }`;
					try {
						this.isMainLoading = true;
						this.isLoading = true;
						const claim = await firstValueFrom(this.allqualityIssueService.getRealRemoteIssue(issue.id));
						claim.pendingPushStatusTr = QualityIssuePendingPushStatus.CANCEL;
						claim.statusTr = QualityIssueStatus.PENDING_NOT_PUSH;
						claim.modifDate = moment().toISOString();
						await this.allqualityIssueService.saveIssue(claim);
						this.snackBar.openFromComponent(
							SnackbarCancelIssueComponent,
							{
								duration: 5000,
								panelClass: ['copo-draft-snackbar']
							}
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					} catch (err) {
						console.error('unable to cancel issue', err);
						this.snackBar.open(
							this.translationService.getTR(
								'error.cancelling.issue'
							),
							undefined,
							{
								duration: 5000,
							}
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					} finally {
						this.isMainLoading = false;
						this.isLoading = false;
					}
				}
			});
	}

	isClosed(claim: QualityIssue) {
		return claim.status !== undefined ? CLOSED_STATUS_LIST.includes(claim.status) : false;
	}

	isModifiable(claim: QualityIssue) {
		return claim.steeluserId
			&& !claim.supplierIssueNumber
			&& !this.isClosed(claim)
			&& claim.issueType !== 'warning';
	}

	getUnitAmount(amountUnitTr?: string) {
		return amountUnitTr
			? CURRENCY_OPTIONS[<keyof typeof CURRENCY_OPTIONS> amountUnitTr.toLowerCase()]
			: CURRENCY_OPTIONS.eur;
	}

	getIssueTypeTr(type: string) {
		if (type === 'warning') {
			return ISSUE_TYPE_TR.warning;
		}
		return ISSUE_TYPE_TR.claim;
	}

	getIssueOriginTr(type: string) {
		return this.translationService.getIssueOriginTr(type);
	}

	getIssueStatusTr(issue?: QualityIssue) {
		return issue?.status !== undefined ? STATUS_TR[issue.status] : STATUS_TR['claimstatus.unknowntype'];
	}

	getQuantityUnit(unit?: string) {
		if (unit && unit.toLowerCase() in UNIT_OPTIONS) {
			return UNIT_OPTIONS[<keyof typeof UNIT_OPTIONS> unit.toLowerCase()].symbol;
		}
		return UNDERSCORE;
	}

	getDefectOption(defect: string) {
		if (defect in DEFECT_DETAILED_OPTIONS) {
			return DEFECT_DETAILED_OPTIONS[<keyof typeof DEFECT_DETAILED_OPTIONS> defect];
		}
		return { label: defect };
	}

	getDecisionTr(issue: QualityIssue) {
		return issue.status !== undefined ? DECISION_TR[issue.status] : DECISION_TR['claimstatus.unknowntype'];
	}

	onGridReady(params: GridReadyEvent) {
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	getCharacteristic(code: string) {
		return this.orderCharacteristics?.find(c => c.code === code);
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	autoScrollToElement(documentEl: HTMLElement) {
		if (this.id === 'documents') {
			documentEl.scrollIntoView();
		}
	}

	private constructAttachements(attachments?: QualityAttachment[]) {
		if (!attachments || attachments.length === 0) {
			return;
		}
		for (const attachment of attachments) {
			attachment.claimid = this.issue ? this.issue.id : undefined;
			attachment.fileName = `${ attachment.name }.${ attachment.extension }`;
		}
		this.attachmentsGridOptions = new CommonGridOptions<QualityAttachment>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			rowData: attachments,
			columnDefs: [
				new CommonColDef<QualityAttachment>({
					headerName: this.translationService.getTR('order-card.type'),
					field: 'extension',
				}),
				new CommonColDef<QualityAttachment>({
					headerName: this.translationService.getTR('order-card.name'),
					field: 'name',
				}),
				new DateColDef<QualityAttachment>({
					headerName: this.translationService.getTR('order.creation.date'),
					field: 'creationDate',
					width: 100,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' }
				}),
				new IconRedirectColDef<QualityAttachment>({
					field: 'name',
					cellRendererParams: {
						linkData: (params: ICellRendererParams<QualityAttachment, unknown>) => {
							const { data } = params;
							if (!data) {
								return undefined;
							}
							const claimid = data.claimid || '';
							const path = data.path || '';
							return {
								url: this.qualityIssueService.getDownloadFile(claimid, path, data.fileName),
								type: data.typeMime,
								icon: 'table_document'
							};
						}
					}
				})
			]
		});
	}

	private constructCommercialDocuments(invoiceResults?: PaginatedResult<Document>) {
		const globalDocuments: GlobalDocument[] = [];
		if (!invoiceResults || invoiceResults.count === 0) {
			return;
		}
		for (const invoice of invoiceResults.results) {
			const invoiceLine: GlobalDocument = {
				title: this.translationService.getTR('order.card.document.invoices'),
				name: invoice.name,
				date: invoice.date,
				id: invoice.id,
				subtype: invoice.subtype ? this.translationService.getTR(`doc-in-subtype.${ invoice.subtype }`)
					: this.translationService.getTR('commercial-documents.invoice'),
				type: 'IN'
			};

			globalDocuments.push(invoiceLine);
		}
		this.gridCommercialDocumentsOptions = new CommonGridOptions<GlobalDocument>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			rowData: globalDocuments,
			columnDefs: [
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.type'),
					field: 'title',
				}),
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('commercial-documents.subtype'),
					field: 'subtype',
				}),
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.name'),
					field: 'name',
				}),
				new DateColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order.creation.date'),
					field: 'date',
					width: 100,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' }
				}),
				new IconRedirectColDef<GlobalDocument>({
					field: 'name',
					cellRendererParams: {
						linkData: (params: ICellRendererParams<GlobalDocument, unknown>) => {
							const { data } = params;
							if (!data) {
								return undefined;
							}
							return {
								url: this.commercialDocumentService.getViewDocumentUrl(data.id, data.name),
								type: 'application/pdf',
								icon: 'table_document'
							};
						}
					}
				})
			]
		});
	}

	private constructBatchesGrid(
		claimBatches: PaginatedResult<ClaimBatch> | undefined
	) {
		const batches = [];
		if (this.issue) {
			const qualityBatches = this.issue.claimType === 'ZR'
				? this.issue.batches.slice(0, 1)
				: this.issue.batches;
			for (const qualityBatch of qualityBatches) {
				let batchRelated: ClaimBatch | undefined;
				if (claimBatches) {
					for (const claimBatch of claimBatches.results) {
						if (claimBatch.batchReference === qualityBatch.batchReference) {
							batchRelated = claimBatch;
						}
					}
				}
				batches.push(new QualityBatchLine(
					qualityBatch.id,
					qualityBatch.batchReference,
					qualityBatch.qty,
					qualityBatch.claimedQty,
					qualityBatch.qtyUnit,
					qualityBatch.claimedQtyUnit,
					batchRelated ? batchRelated.qtyBatchNet : undefined,
					batchRelated ? batchRelated.qtyBatchGross : undefined,
					batchRelated ? batchRelated.lastDispatchAdviceNumber : undefined,
					batchRelated ? batchRelated.lastDispatchAdviceDate : undefined,
					batchRelated
				));
			}
		}
		if (batches.length === 0) {
			return;
		}
		this.gridBatchesOptions = new CommonGridOptions<QualityBatchLine>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<QualityBatchLine>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'reference',
					cellRendererParams: linkToBatchCardCellRendererParams,
				}),
				new CommonColDef<QualityBatchLine>({
					type: 'numericColumn',
					headerName: this.translationService.getTR('claim.impacted.quantity'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const { quantityUnitTr, netWeight } = params.data;
						let { quantityValue } = params.data;
						const unit = quantityUnitTr
							? <keyof typeof UNIT_OPTIONS> quantityUnitTr.toLowerCase()
							: '';
						const realUnit = unit && unit in UNIT_OPTIONS
							? unit
							: 'to';
						quantityValue = unit === realUnit || !netWeight ? quantityValue : netWeight;
						const impacted = `${
							formatNumber(params.data.claimedQuantityValue, 'weight-with-zero')
						}${
							formatHiddenNumber(params.data.claimedQuantityValue, 'weight-with-zero')
						} /\u00A0`;
						return `${ params.data.claimedQuantityValue < quantityValue ? impacted : '' }${
							formatNumber(quantityValue, 'weight-with-zero')
						}${
							formatHiddenNumber(quantityValue, 'weight-with-zero')
						}\u00A0${
							UNIT_OPTIONS[realUnit].symbol
						}`;
					}
				}),
				new NumericColDef<QualityBatchLine>({
					headerName: this.translationService.getTR('batch.gross'),
					field: 'grossWeight',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<QualityBatchLine>({
					headerName: this.translationService.getTR('batch.net'),
					field: 'netWeight',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new DateColDef<QualityBatchLine>({
					headerName: this.translationService.getTR('shipment.dispatch-date'),
					field: 'dispatchDate'
				}),
				new CommonColDef<QualityBatchLine>({
					headerName: this.translationService.getTR('shipment.dispatch-advice'),
					field: 'dispatchAdvice'
				})
			]
		});
	}

	private constructDocumentFilter(supplierClaimReference: string) {
		const supplierClaimReferenceField: FieldExpression = {
			field: 'supplierClaimReference',
			term: supplierClaimReference,
			quoted: true
		};

		const subTypeField: FieldExpression = {
			field: 'subtype',
			term: 'AVOIR',
			quoted: true
		};

		const fullCondition: NodeExpression = {
			left: supplierClaimReferenceField,
			operator: 'AND',
			right: subTypeField,
			parenthesized: true
		};

		return luceneToString(fullCondition);
	}
}
